import { z } from 'zod';

import {
	PhoneWithEnumedPrefixV3x1PrefixEnum,
	ProcessTypeV3x1,
} from '../../../../../../api/generated/cz.partners.pid.backofficegateway.pidonboarding';
import { UUID_SCHEMA } from '../../../../../../const/zod/zod';
import { createDateSchemaWithDefinedMinAndMaxBankValues } from '../../../../../../helpers/zod/createDateSchemaWithDefinedMinAndMaxBankValues';
import { createEnumArraySchema } from '../../../../../../helpers/zod/createEnumArraySchema';
import { validateDateRange } from '../../../../../../helpers/zod/validateDateRange';
import { checkIsBankIdType } from '../../../helpers/checkIsBankIdType';
import { DEVICE_AND_APPLICATION_NAME_MAX_STRING_LENGTH } from '../const';

const PHONE_NUMBER_LENGTH_WITHOUT_PREFIX = 9;

export type ActivationRequestStateKeys =
	keyof typeof ACTIVATIONS_REQUEST_STATES;

export const ACTIVATIONS_REQUEST_STATES = {
	onlyUncompleted: 'onlyUncompleted',
	manualIdentityVerificationAllowed: 'manualIdentityVerificationAllowed',
	allActivationRequestStates: 'allActivationRequestStates',
} as const;

export type ApplicationsActivationsFilterMessages = {
	wrongActivationStateError: string;
	maxStringLengthError: string;
	userSubFilterOnlyWithBankIdError: string;
	completedAtToBeforeFromError: string;
	dateFromError: string;
	dateToError: string;
	dateCantBeEarlierThanBankInceptionDateError: string;
};

export const getApplicationsActivationsFilterFormSchema = (
	errorMessages?: ApplicationsActivationsFilterMessages,
) => {
	const schema = z
		.object({
			pidUuid: UUID_SCHEMA,
			completedAtFrom: createDateSchemaWithDefinedMinAndMaxBankValues({
				minDateErrorMessage:
					errorMessages?.dateCantBeEarlierThanBankInceptionDateError,
				maxDateErrorMessage: errorMessages?.dateFromError,
			}),
			completedAtTo: createDateSchemaWithDefinedMinAndMaxBankValues({
				minDateErrorMessage:
					errorMessages?.dateCantBeEarlierThanBankInceptionDateError,
				maxDateErrorMessage: errorMessages?.dateToError,
			}),
			processTypes: createEnumArraySchema(ProcessTypeV3x1),
			activationsState: z.enum(ACTIVATIONS_REQUEST_STATES),
			phone: z
				.object({
					prefix: z.enum(PhoneWithEnumedPrefixV3x1PrefixEnum),
					value: z
						.string()
						.length(PHONE_NUMBER_LENGTH_WITHOUT_PREFIX)
						.or(z.literal('')),
				})
				.nullable(),
			userSub: UUID_SCHEMA,
			deviceName: z
				.string()
				.max(DEVICE_AND_APPLICATION_NAME_MAX_STRING_LENGTH, {
					message: errorMessages?.maxStringLengthError,
				})
				.nullable(),
			activationName: z
				.string()
				.max(DEVICE_AND_APPLICATION_NAME_MAX_STRING_LENGTH, {
					message: errorMessages?.maxStringLengthError,
				})
				.nullable(),
		})
		.superRefine(({ processTypes, activationsState, userSub }, ctx) => {
			const isBankIdProcessType =
				processTypes.some(checkIsBankIdType) || processTypes.length === 0;

			const isDeviceQrActivationProcessType =
				processTypes.some(
					(type) => type === ProcessTypeV3x1.DEVICE_QR_ACTIVATION,
				) || processTypes.length === 0;

			const isOnlyBankIdProcessType = processTypes.some(checkIsBankIdType);

			if (!isOnlyBankIdProcessType && userSub) {
				ctx.addIssue({
					code: 'custom',
					message: errorMessages?.userSubFilterOnlyWithBankIdError,
					path: ['userSub'],
				});
			}

			const isManualIdentityVerificationAllowed =
				activationsState ===
				ACTIVATIONS_REQUEST_STATES.manualIdentityVerificationAllowed;

			const isWrongActivationProcessType =
				isBankIdProcessType || isDeviceQrActivationProcessType;
			const isWrongActivationStateError =
				isWrongActivationProcessType && isManualIdentityVerificationAllowed;

			if (isWrongActivationStateError) {
				ctx.addIssue({
					code: 'custom',
					message: errorMessages?.wrongActivationStateError,
					path: ['activationsState'],
				});
			}
		})
		.superRefine(({ completedAtFrom, completedAtTo }, ctx) => {
			validateDateRange(
				completedAtFrom,
				completedAtTo,
				errorMessages?.completedAtToBeforeFromError,
				ctx,
				'completedAtTo',
			);
		});

	return schema;
};

export type ApplicationsActivationsFilterForm = z.infer<
	ReturnType<typeof getApplicationsActivationsFilterFormSchema>
>;
